import { 
    IonButton, 
    IonCol, 
    IonGrid, 
    IonInput, 
    IonItem, 
    IonLabel, 
    IonRow, 
    IonSpinner, 
    IonTextarea,
    IonToast,
    IonIcon,
    useIonViewDidEnter 
} from '@ionic/react';
import { cloudDownload, cloudUpload } from 'ionicons/icons';
import React, { useRef, useState } from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import { apiBaseUrl } from '../../configs/app';
import { useAppContext } from '../../services/app-context.service';
import Settings from '../../types/settngs.type';

const SettingsPage = () => {
    const store = useAppContext() as any;
    const fileInputRef = useRef<HTMLInputElement>(null);
    const [config, setConfig] = useState<Settings>({
        company_email: '',
        company_phone: '',
        company_address: '',
        bdt_per_rmb: 0,
        profit_per_sale: 0,
        shipping_instruction_1: '',
        shipping_instruction_2: '',
        shipping_instruction_3: '',
        shipping_instruction_4: '',
    });
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [showToast, setShowToast] = useState(false);
    const [toastMessage, setToastMessage] = useState('');
    const [toastColor, setToastColor] = useState('success');

    const fetchConfig = () => {
        setLoading(true);
        const myHeaders = new Headers();
        myHeaders.append("Accept", "application/json");
        myHeaders.append("Authorization", "Bearer " + store.user.token);

        const requestOptions = {
            method: 'GET',
            headers: myHeaders,
        } as any;

        fetch(apiBaseUrl + "admin/frontend-config", requestOptions)
            .then((res) => res.json())
            .then((data) => {
                setConfig(data);
            })
            .catch((err) => {
                console.log(err);
                setToastMessage('Failed to load configuration');
                setToastColor('danger');
                setShowToast(true);
            })
            .finally(() => setLoading(false));
    };

    useIonViewDidEnter(() => {
        fetchConfig();
    });

    const handleSave = () => {
        setSaving(true);
        const myHeaders = new Headers();
        myHeaders.append("Accept", "application/json");
        myHeaders.append("Content-Type", "application/json");
        myHeaders.append("Authorization", "Bearer " + store.user.token);

        const requestOptions = {
            method: 'POST',
            headers: myHeaders,
            body: JSON.stringify(config),
        } as any;

        fetch(apiBaseUrl + "admin/frontend-config", requestOptions)
            .then((res) => res.json())
            .then((data) => {
                if (data.message) {
                    setToastMessage(data.message);
                    setToastColor('success');
                    setShowToast(true);
                    // Reload the config from server
                    fetchConfig();
                } else if (data.errors) {
                    setToastMessage('Validation errors occurred');
                    setToastColor('danger');
                    setShowToast(true);
                }
            })
            .catch((err) => {
                console.log(err);
                setToastMessage('Failed to save configuration');
                setToastColor('danger');
                setShowToast(true);
            })
            .finally(() => setSaving(false));
    };

    const handleInputChange = (field: keyof Settings, value: any) => {
        setConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleExport = () => {
        const dataStr = JSON.stringify(config, null, 2);
        const dataBlob = new Blob([dataStr], { type: 'application/json' });
        const url = URL.createObjectURL(dataBlob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `frontend-config-${new Date().toISOString().split('T')[0]}.json`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
        
        setToastMessage('Configuration exported successfully');
        setToastColor('success');
        setShowToast(true);
    };

    const handleImportClick = () => {
        fileInputRef.current?.click();
    };

    const handleImport = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        if (!file.name.endsWith('.json')) {
            setToastMessage('Please select a valid JSON file');
            setToastColor('danger');
            setShowToast(true);
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const importedConfig = JSON.parse(e.target?.result as string);
                
                // Validate the imported config has required fields
                const requiredFields: (keyof Settings)[] = [
                    'company_email',
                    'company_phone',
                    'company_address',
                    'bdt_per_rmb',
                    'profit_per_sale',
                    'shipping_instruction_1',
                    'shipping_instruction_2',
                    'shipping_instruction_3',
                    'shipping_instruction_4'
                ];
                
                const missingFields = requiredFields.filter(field => !(field in importedConfig));
                
                if (missingFields.length > 0) {
                    setToastMessage(`Missing fields: ${missingFields.join(', ')}`);
                    setToastColor('danger');
                    setShowToast(true);
                    return;
                }
                
                setConfig(importedConfig);
                setToastMessage('Configuration imported successfully. Click Save to apply changes.');
                setToastColor('warning');
                setShowToast(true);
            } catch (error) {
                setToastMessage('Failed to parse JSON file. Please check the file format.');
                setToastColor('danger');
                setShowToast(true);
            }
        };
        
        reader.readAsText(file);
        
        // Reset the input so the same file can be selected again
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    return (
        <AdminLayout title="Frontend Settings">
            <IonGrid>
                <IonRow>
                    <IonCol size="12" sizeMd="10" offsetMd="1" sizeLg="8" offsetLg="2">
                        {loading ? (
                            <div style={{ textAlign: 'center', padding: '50px' }}>
                                <IonSpinner />
                            </div>
                        ) : (
                            <>
                                <div style={{ padding: '20px 0', display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap' }}>
                                    <IonButton color="secondary" onClick={handleExport}>
                                        <IonIcon slot="start" icon={cloudDownload} />
                                        Export JSON
                                    </IonButton>
                                    <IonButton color="tertiary" onClick={handleImportClick}>
                                        <IonIcon slot="start" icon={cloudUpload} />
                                        Import JSON
                                    </IonButton>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".json,application/json"
                                        onChange={handleImport}
                                        style={{ display: 'none' }}
                                    />
                                </div>
                                
                                <h2>Company Information</h2>
                                <IonItem>
                                        <IonLabel position="floating">Company Email</IonLabel>
                                        <IonInput
                                            type="email"
                                            value={config.company_email}
                                            onIonChange={(e) => handleInputChange('company_email', e.detail.value)}
                                        />
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel position="floating">Company Phone</IonLabel>
                                        <IonInput
                                            type="tel"
                                            value={config.company_phone}
                                            onIonChange={(e) => handleInputChange('company_phone', e.detail.value)}
                                        />
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel position="floating">Company Address</IonLabel>
                                        <IonTextarea
                                            rows={3}
                                            value={config.company_address}
                                            onIonChange={(e) => handleInputChange('company_address', e.detail.value)}
                                        />
                                    </IonItem>

                                    <h2 style={{ marginTop: '30px' }}>Pricing Configuration</h2>
                                    <IonItem>
                                        <IonLabel position="floating">BDT per RMB</IonLabel>
                                        <IonInput
                                            type="number"
                                            value={config.bdt_per_rmb}
                                            onIonChange={(e) => handleInputChange('bdt_per_rmb', parseFloat(e.detail.value || '0'))}
                                        />
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel position="floating">Profit per Sale (Multiplier)</IonLabel>
                                        <IonInput
                                            type="number"
                                            step="0.01"
                                            value={config.profit_per_sale}
                                            onIonChange={(e) => handleInputChange('profit_per_sale', parseFloat(e.detail.value || '0'))}
                                        />
                                    </IonItem>

                                    <h2 style={{ marginTop: '30px' }}>Shipping Instructions</h2>
                                    <IonItem>
                                        <IonLabel position="floating">Shipping Instruction 1</IonLabel>
                                        <IonTextarea
                                            rows={2}
                                            value={config.shipping_instruction_1}
                                            onIonChange={(e) => handleInputChange('shipping_instruction_1', e.detail.value)}
                                        />
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel position="floating">Shipping Instruction 2 (Category A)</IonLabel>
                                        <IonTextarea
                                            rows={3}
                                            value={config.shipping_instruction_2}
                                            onIonChange={(e) => handleInputChange('shipping_instruction_2', e.detail.value)}
                                        />
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel position="floating">Shipping Instruction 3 (Category B)</IonLabel>
                                        <IonTextarea
                                            rows={3}
                                            value={config.shipping_instruction_3}
                                            onIonChange={(e) => handleInputChange('shipping_instruction_3', e.detail.value)}
                                        />
                                    </IonItem>
                                    <IonItem>
                                        <IonLabel position="floating">Shipping Instruction 4 (Category C)</IonLabel>
                                        <IonTextarea
                                            rows={3}
                                            value={config.shipping_instruction_4}
                                            onIonChange={(e) => handleInputChange('shipping_instruction_4', e.detail.value)}
                                        />
                                    </IonItem>

                                    <div style={{ padding: '20px', textAlign: 'center' }}>
                                        <IonButton 
                                            expand="block" 
                                            onClick={handleSave}
                                            disabled={saving}
                                        >
                                            {saving ? <IonSpinner /> : 'Save Configuration'}
                                        </IonButton>
                                    </div>
                                </>
                            )}
                        </IonCol>
                    </IonRow>
                </IonGrid>

                <IonToast
                    isOpen={showToast}
                    onDidDismiss={() => setShowToast(false)}
                    message={toastMessage}
                    duration={3000}
                    color={toastColor}
                />
            </AdminLayout>
        );
    };
    
    export default SettingsPage;